import React from 'react';
import { Text } from 'react-native';
import PropTypes from 'prop-types';

import * as accessibilityRoles from '../../modules/accessbilityRoles';

import './HeaderText.module.scss';

/**
 * Allows for display of header text using a particular client or provider header level based on
 * design guidelines available here (for client)
 * https://styleseat.atlassian.net/wiki/spaces/PROD/pages/78807073/Client+Side+Design+Guidelines
 *
 * and here (for provider)
 * https://app.zeplin.io/project/576dbcd42bbb9162542be76d/screen/5aeca01bbea3aab2be02d69e
 *
 * The header text component *MUST* be enclosed in a View component
 *
 * To translate design font sizes to (client) header level, use this quick table:
 * Level 1: 24px
 * Level 2: 20px
 * Level 3: 16px
 * Level 4: 14px
 *
 * @param {object} restProps
 * @param {number} [restProps.level = 1]
 * @param {'client' | 'provider'} [restProps.theme = 'client'] Deprecated; provider is going away
 */
const HeaderText = ({
  children,
  level,
  ariaLevel = level,
  theme,
  className,
  style,
  textAlign,
  color,
  fontSize,
  lineHeight,
  whiteSpace,
  weight: fontWeight,
  textTransform,
  textDecoration,
  testID,
  ...restProps
}) => {
  const textDecorationLine = textDecoration || style?.textDecoration;
  const textColor = color || style?.color;
  return (
    <Text
      accessibilityRole={accessibilityRoles.HeaderText}
      aria-level={ariaLevel}
      styleName={`header-text-component ${theme}-header${level}`}
      className={className}
      style={[style, {
        textAlign: textAlign || style?.textAlign,
        fontSize: fontSize || style?.fontSize,
        lineHeight: lineHeight || style?.lineHeight,
        color: textColor,
        whiteSpace: whiteSpace || style?.whiteSpace,
        fontWeight: fontWeight || style?.fontWeight,
        textTransform: textTransform || style?.textTransform,
        textDecorationLine,
        textDecorationStyle: textDecorationLine ? 'solid' : undefined,
        textDecorationColor: textDecorationLine ? textColor : undefined,
      }]}
      testID={testID}
      {...restProps}
    >
      {children}
    </Text>
  );
};

export const ARIA_LEVEL_OPTIONS = [
  '1', 1,
  '2', 2,
  '3', 3,
  '4', 4,
  '5', 5,
];

export const LEVEL_OPTIONS = [
  '1', 1,
  '2', 2,
  '3', 3,
  '4', 4,
];

export const THEME_OPTIONS = [
  'client',
  'provider',
  'notification',
];

export const TEXT_TRANSFORM_OPTIONS = [
  'none',
  'capitalize',
  'uppercase',
  'lowercase',
];

export const TEXT_ALIGN_OPTIONS = [
  'auto',
  'left',
  'right',
  'center',
  'justify',
];

export const TEXT_DECORATION_OPTIONS = [
  'none',
  'overline',
  'line-through',
  'underline',
];

const WEIGHT_OPTIONS = [
  'thin',
  'normal',
  'bold',
  'extra-bold',
  'heavy',
  'medium',
  undefined,
];

HeaderText.propTypes = {
  ariaLevel: PropTypes.oneOf(ARIA_LEVEL_OPTIONS),
  /** Text to display */
  children: PropTypes.node,
  level: PropTypes.oneOf(LEVEL_OPTIONS),
  /** @deprecated Do not use the provider theme */
  theme: PropTypes.oneOf(THEME_OPTIONS),
  className: PropTypes.string,
  style: PropTypes.any,
  color: PropTypes.string,
  fontSize: PropTypes.number,
  lineHeight: PropTypes.number,
  textTransform: PropTypes.oneOf(TEXT_TRANSFORM_OPTIONS),
  /** How to align the text */
  textAlign: PropTypes.oneOf(TEXT_ALIGN_OPTIONS),
  textDecoration: PropTypes.oneOf(TEXT_DECORATION_OPTIONS),
  whiteSpace: PropTypes.string,
  weight: PropTypes.oneOf(WEIGHT_OPTIONS),
  testID: PropTypes.string,
};

HeaderText.defaultProps = {
  ariaLevel: undefined,
  children: null,
  level: '1',
  theme: 'client',
  className: undefined,
  style: undefined,
  textAlign: undefined,
  color: undefined,
  fontSize: undefined,
  lineHeight: undefined,
  whiteSpace: undefined,
  weight: undefined,
  textTransform: undefined,
  textDecoration: undefined,
  testID: '',
};

export default HeaderText;
